import { DeployEnv, Site, SLD } from '@patsnap-utils/biz';

/**
 * LLM 模型选择工具
 *
 * 模型由 home 的 Agentic 模型切换组件写入 localStorage，
 * 各业务项目在请求底层（PsHttpService.options.headers）读取并注入 header。
 *
 * 存储约定必须与 home/src/utils/agentic-search/llm-model.ts 保持一致。
 */

export const AUTO_LLM_MODEL = 'Auto';
export const DEFAULT_LLM_MODEL = AUTO_LLM_MODEL;
export const GLM_LLM_MODEL = 'glm-5.2';

export const LLM_MODEL_STORAGE_PREFIX = 'rd-home-sdk:llm-model';
const LLM_MODEL_GLM_MIGRATION_PREFIX = 'rd-home-sdk:llm-model:glm-5.2-enabled';

/**
 * 各业务项目对应的 functionId（与 home 的 AgenticFunctionId 枚举一致）。
 * 用作读取 localStorage 缓存的 key 后缀。
 */
export const LLM_FUNCTION_ID = {
    TRIZ: 'rd_triz',
    QUICK_RESEARCH: 'rd_research',
    TECH_MIND: 'rd_air_find_solution_agent',
    FMEA: 'rd_fmea',
} as const;

/**
 * 已开放 GLM 的业务。后续开放新业务时只需在此处增加对应 functionId。
 */
export const GLM_ENABLED_FUNCTION_IDS: readonly string[] = [
    LLM_FUNCTION_ID.TRIZ
];

export function isGlmModelAvailable(functionId?: string): boolean {
    return Boolean(functionId && GLM_ENABLED_FUNCTION_IDS.includes(functionId));
}

export function getDefaultLlmModel(functionId?: string): string {
    return isGlmModelAvailable(functionId)
        ? GLM_LLM_MODEL
        : DEFAULT_LLM_MODEL;
}

export function isLlmModelEnabled(): boolean {
    try {
        const env = new Site({ sld: SLD.EUREKA }).env;
        return env !== DeployEnv.US && env !== DeployEnv.EU;
    } catch {
        return true;
    }
}

export function getLlmModelStorageKey(functionId: string): string {
    return `${LLM_MODEL_STORAGE_PREFIX}:${functionId}`;
}

function getLlmModelMigrationStorageKey(functionId: string): string {
    return `${LLM_MODEL_GLM_MIGRATION_PREFIX}:${functionId}`;
}

/**
 * 项目首次开放 GLM 时，把旧的默认 Auto 迁移到新的默认 GLM。
 * 迁移只执行一次；QWEN 等明确选择不会被覆盖，迁移后用户再次选择 Auto 也会被保留。
 */
function migrateLegacyAutoSelectionToGlm(functionId: string): void {
    if (!isGlmModelAvailable(functionId)) return;

    const migrationKey = getLlmModelMigrationStorageKey(functionId);
    if (localStorage.getItem(migrationKey) === '1') return;

    const modelStorageKey = getLlmModelStorageKey(functionId);
    if (localStorage.getItem(modelStorageKey) === AUTO_LLM_MODEL) {
        localStorage.removeItem(modelStorageKey);
    }
    localStorage.setItem(migrationKey, '1');
}

/**
 * 归一化模型值：空值或非法值回退到调用方指定的默认模型。
 * @param validValues 可选的合法模型值列表（由 SDK 的 LLM_MODEL_OPTIONS 派生传入）。
 *        不传时仅校验非空，信任写入侧已归一过的缓存值。
 */
export function normalizeLlmModel(
    model?: string | null,
    validValues?: string[],
    defaultModel = DEFAULT_LLM_MODEL
): string {
    if (!model) return defaultModel;
    if (validValues && !validValues.includes(model)) return defaultModel;
    return model;
}

function normalizeLlmModelForFunction(
    functionId: string,
    model?: string | null,
    validValues?: string[]
): string {
    const defaultModel = getDefaultLlmModel(functionId);
    const normalizedModel = normalizeLlmModel(model, validValues, defaultModel);

    return normalizedModel === GLM_LLM_MODEL && !isGlmModelAvailable(functionId)
        ? defaultModel
        : normalizedModel;
}

export function getStoredLlmModel(functionId?: string, validValues?: string[]): string {
    if (!functionId) return DEFAULT_LLM_MODEL;
    if (typeof localStorage === 'undefined') return getDefaultLlmModel(functionId);
    try {
        migrateLegacyAutoSelectionToGlm(functionId);
        return normalizeLlmModelForFunction(
            functionId,
            localStorage.getItem(getLlmModelStorageKey(functionId)),
            validValues
        );
    } catch {
        return getDefaultLlmModel(functionId);
    }
}

export function setStoredLlmModel(functionId: string, model: string, validValues?: string[]): void {
    if (!functionId || typeof localStorage === 'undefined') return;
    try {
        if (isGlmModelAvailable(functionId)) {
            localStorage.setItem(getLlmModelMigrationStorageKey(functionId), '1');
        }
        localStorage.setItem(
            getLlmModelStorageKey(functionId),
            normalizeLlmModelForFunction(functionId, model, validValues)
        );
    } catch {
        // ignore storage write failure
    }
}

export function removeStoredLlmModel(functionId?: string): void {
    if (!functionId || typeof localStorage === 'undefined') return;
    try {
        localStorage.removeItem(getLlmModelStorageKey(functionId));
    } catch {
        // ignore storage removal failure
    }
}

/**
 * 生成请求 header，Auto 或未启用时返回空对象（由后端使用默认模型）。
 * @param functionId 业务项目写死的 functionId，如 LLM_FUNCTION_ID.TRIZ
 */
export function getLlmModelHeader(functionId?: string): Record<string, string> {
    if (!functionId || !isLlmModelEnabled()) return {};

    const model = getStoredLlmModel(functionId);
    return model === AUTO_LLM_MODEL ? {} : { 'X-Select-Model': model };
}

/**
 * 获取传给请求 body 的 model 参数（值与公司模型网关保持一致）。
 * Auto 或未启用时不传，由后端使用默认模型。
 */
export function getLlmModelParam(functionId?: string): { model?: string } {
    if (!functionId || !isLlmModelEnabled()) return {};

    const model = getStoredLlmModel(functionId);
    return model === AUTO_LLM_MODEL ? {} : { model };
}
